<template>
  <div class="enterprise-login-content">
    <!-- Login Method Tabs -->
    <div class="login-method-tabs">
      <span
        class="method-tab"
        :class="{ active: loginMethod === 'phone' }"
        @click="switchLoginMethod('phone')"
      >
        管理员手机号登录
      </span>
      <span class="method-divider">|</span>
      <span
        class="method-tab"
        :class="{ active: loginMethod === 'account' }"
        @click="switchLoginMethod('account')"
      >
        企业账号登录
      </span>
    </div>

    <!-- Admin Phone Login -->
    <div
      v-if="loginMethod === 'phone'"
      class="phone-login-wrapper"
    >
      <el-form
        ref="phoneLoginForm"
        :model="phoneLoginForm"
        :rules="phoneLoginRules"
        class="phone-login-form"
      >
        <el-form-item prop="phonenumber">
          <PhoneInput
            v-model="phoneLoginForm.phonenumber"
            placeholder="请输入手机号"
            @keyup.enter="handleLogin"
          />
        </el-form-item>
        <el-form-item prop="smsCode">
          <el-input
            v-model="phoneLoginForm.smsCode"
            placeholder="请输入验证码"
            clearable
            maxlength="6"
            @keyup.enter="handleLogin"
          >
            <template #append>
              <el-button
                :disabled="smsCodeCountdown > 0"
                :loading="smsCodeLoading"
                @click="handleGetSmsCode"
              >
                {{ smsCodeCountdown > 0 ? `${smsCodeCountdown}秒后重试` : '获取验证码' }}
              </el-button>
            </template>
          </el-input>
        </el-form-item>
      </el-form>
      <div class="phone-login-options">
        <a
          href="#"
          class="no-code-link"
          @click.prevent="handleNoCodeClick"
        >收不到验证码？</a>
      </div>
    </div>

    <!-- Enterprise Account Login -->
    <div
      v-if="loginMethod === 'account'"
      class="account-login-wrapper"
    >
      <el-form
        ref="accountLoginForm"
        :model="accountLoginForm"
        :rules="accountLoginRules"
        class="account-login-form"
      >
        <el-form-item prop="account">
          <el-input
            v-model="accountLoginForm.account"
            placeholder="请输入企业账号"
            clearable
            @keyup.enter="handleLogin"
          />
        </el-form-item>
        <el-form-item prop="password">
          <el-input
            v-model="accountLoginForm.password"
            type="password"
            placeholder="请输入密码"
            show-password
            clearable
            @keyup.enter="handleLogin"
          />
        </el-form-item>
      </el-form>
      <div class="account-login-options">
        <el-checkbox v-model="rememberMe">
          记住我
        </el-checkbox>
        <a
          href="#"
          class="forgot-password-link"
          @click.prevent="handleForgotPassword"
        >忘记密码？</a>
      </div>
    </div>

    <!-- Agreement Checkbox -->
    <div class="agreement-section">
      <el-checkbox v-model="agreedToTerms">
        <span class="agreement-text">
          勾选即为同意
          <a
            href="#"
            class="agreement-link"
            @click.prevent="$emit('privacy-click')"
          >《隐私协议》</a>
          和
          <a
            href="#"
            class="agreement-link"
            @click.prevent="$emit('register-click')"
          >《注册协议》</a>
        </span>
      </el-checkbox>
    </div>

    <!-- Login Button -->
    <div class="login-button-wrapper">
      <el-button
        type="primary"
        class="login-btn"
        :disabled="!agreedToTerms"
        :loading="loginLoading"
        @click="handleLogin"
      >
        登录
      </el-button>
    </div>

    <EnterpriseForgotPasswordDialog
      v-model="forgotPasswordDialogVisible"
      :initial-account="accountLoginForm.account"
    />

    <!-- 企业登录：仅选择企业列表（不展示个人身份） -->
    <SelectIdentityDialog
      v-model:visible="identityDialogVisible"
      mode="enterprise"
      :identity-data="loginIdentityData"
      :closable="false"
      @success="handleIdentitySelectSuccess"
    />
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { enterpriseAccountLogin, enterprisePersonalAdminSmsLogin, switchUser } from '@/api/login'
import { getSmsCaptcha } from '@/api/utils/captcha'
import PhoneInput from '@/components/PhoneInput/index.vue'
import SelectIdentityDialog from '@/components/SelectIdentityDialog/index.vue'
import EnterpriseForgotPasswordDialog from './EnterpriseForgotPasswordDialog.vue'
import { GLOBAL_PHONE_PATTERN } from '@/utils/idValidator'

// 常量配置
const COUNTDOWN_TIME = 60 // 验证码倒计时时间（秒）
const RELOAD_DELAY = 1000 // 页面刷新延迟（毫秒）
const STORAGE_KEY_ACCOUNT = 'enterprise_login_account' // localStorage 存储键名

export default {
  name: 'EnterpriseLogin',
  components: { PhoneInput, EnterpriseForgotPasswordDialog, SelectIdentityDialog },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['login-success', 'privacy-click', 'register-click'],
  data() {
    // 手机号验证规则
    const validatePhone = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入手机号'))
      } else if (!GLOBAL_PHONE_PATTERN.test(value)) {
        callback(new Error('请输入正确的手机号'))
      } else {
        callback()
      }
    }

    return {
      loginMethod: 'phone', // 'phone' or 'account'
      agreedToTerms: false,
      rememberMe: false,
      loginLoading: false,
      forgotPasswordDialogVisible: false,
      identityDialogVisible: false,
      loginIdentityData: null,

      // Phone Login Form
      phoneLoginForm: {
        phonenumber: '',
        smsCode: ''
      },
      phoneLoginRules: {
        phonenumber: [
          { required: true, validator: validatePhone, trigger: 'blur' }
        ],
        smsCode: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { len: 6, message: '验证码为6位数字', trigger: 'blur' }
        ]
      },
      smsCodeLoading: false,
      smsCodeCountdown: 0,
      smsCodeTimer: null,

      // Account Login Form
      accountLoginForm: {
        account: '',
        password: ''
      },
      accountLoginRules: {
        account: [
          { required: true, message: '请输入企业账号', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm()
        this.loadRememberedAccount()
      } else {
        this.clearTimers()
      }
    },
    loginMethod(newVal) {
      if (newVal === 'account') {
        this.loadRememberedAccount()
      }
    }
  },
  mounted() {
    this.loadRememberedAccount()
  },
  beforeUnmount() {
    this.clearTimers()
  },
  methods: {
    // ==================== 工具方法 ====================
    /**
     * 清除所有定时器
     */
    clearTimers() {
      if (this.smsCodeTimer) {
        clearInterval(this.smsCodeTimer)
        this.smsCodeTimer = null
        this.smsCodeCountdown = 0
      }
    },
    /**
     * 切换登录方式
     * @param {String} method - 登录方式 ('phone' | 'account')
     */
    switchLoginMethod(method) {
      this.loginMethod = method
      // 清除验证状态
      this.$nextTick(() => {
        if (method === 'phone' && this.$refs.phoneLoginForm) {
          this.$refs.phoneLoginForm.clearValidate()
        } else if (method === 'account' && this.$refs.accountLoginForm) {
          this.$refs.accountLoginForm.clearValidate()
        }
      })
    },
    /**
     * 重置表单和状态
     */
    resetForm() {
      this.loginMethod = 'phone'
      this.agreedToTerms = false
      this.rememberMe = false
      this.forgotPasswordDialogVisible = false
      this.phoneLoginForm = { phonenumber: '', smsCode: '' }
      this.accountLoginForm = { account: '', password: '' }
      
      // 加载记住的账号
      this.loadRememberedAccount()
      
      // 清除表单验证
      this.$refs.phoneLoginForm?.clearValidate()
      this.$refs.accountLoginForm?.clearValidate()
      
      // 清除定时器
      this.clearTimers()
    },
    // ==================== 缓存管理 ====================
    /**
     * 从缓存加载记住的账号
     */
    loadRememberedAccount() {
      try {
        const rememberedAccount = localStorage.getItem(STORAGE_KEY_ACCOUNT)
        if (rememberedAccount) {
          this.accountLoginForm.account = rememberedAccount
        }
      } catch (error) {
        console.error('加载记住的账号失败:', error)
      }
    },
    /**
     * 保存账号到缓存
     * @param {String} account - 企业账号
     */
    saveRememberedAccount(account) {
      try {
        if (this.rememberMe && account) {
          localStorage.setItem(STORAGE_KEY_ACCOUNT, account)
        } else {
          localStorage.removeItem(STORAGE_KEY_ACCOUNT)
        }
      } catch (error) {
        console.error('保存记住的账号失败:', error)
      }
    },
    // ==================== 手机号登录相关 ====================
    /**
     * 获取手机验证码
     */
    async handleGetSmsCode() {
      await this.$nextTick()
      
      const form = this.$refs.phoneLoginForm
      if (!form) {
        ElMessage.error('表单引用不存在，请刷新页面重试')
        return
      }

      // 验证手机号字段（validateField 回调接收 errorMessage 和 invalidFields 两个参数）
      form.validateField('phonenumber', async (errorMessage) => {
        // 如果有错误消息（字符串类型且不为空），说明验证失败
        // 注意：errorMessage 应该是字符串，空字符串表示验证通过
        if (errorMessage && typeof errorMessage === 'string' && errorMessage.trim() !== '') {
          ElMessage.error(errorMessage)
          return
        }

        // 额外检查手机号是否为空（防止验证器未正确触发）
        const phonenumber = this.phoneLoginForm?.phonenumber || ''
        if (!phonenumber || phonenumber.trim() === '') {
          ElMessage.warning('请输入手机号')
          return
        }

        // 验证通过，发送验证码
        this.smsCodeLoading = true
        try {
          const response = await getSmsCaptcha(phonenumber.trim())
          
          if (response && response.code === 200) {
            ElMessage.success('验证码已发送')
            this.startCountdown()
          }
          // 全局拦截器已处理错误消息显示，这里不需要额外处理
        } catch (error) {
          // 全局拦截器已显示错误消息，这里只记录日志
          console.error('获取验证码失败:', error)
        } finally {
          this.smsCodeLoading = false
        }
      })
    },
    /**
     * 启动验证码倒计时
     */
    startCountdown() {
      if (this.smsCodeTimer) {
        clearInterval(this.smsCodeTimer)
        this.smsCodeTimer = null
      }
      
      this.smsCodeCountdown = COUNTDOWN_TIME
      this.smsCodeTimer = setInterval(() => {
        this.smsCodeCountdown--
        if (this.smsCodeCountdown <= 0) {
          clearInterval(this.smsCodeTimer)
          this.smsCodeTimer = null
          this.smsCodeCountdown = 0
        }
      }, 1000)
    },
    /**
     * 处理"收不到验证码"点击
     */
    handleNoCodeClick() {
      ElMessage.info('如未收到验证码，请检查手机号是否正确或稍后重试')
    },
    // ==================== 登录处理 ====================
    /**
     * 统一登录入口
     */
    async handleLogin() {
      if (!this.agreedToTerms) {
        ElMessage.warning('请先同意隐私协议和注册协议')
        return
      }

      this.loginLoading = true
      try {
        if (this.loginMethod === 'account') {
          await this.handleAccountLogin()
        } else {
          await this.handlePhoneLogin()
        }
      } catch (error) {
        // 全局拦截器已显示错误消息，这里只记录日志
        console.error('登录失败:', error)
      } finally {
        this.loginLoading = false
      }
    },
    /**
     * 手机号验证码登录（管理员）：有多家企业时仅弹出企业列表选择
     */
    async handlePhoneLogin() {
      await this.$refs.phoneLoginForm.validate()

      const response = await enterprisePersonalAdminSmsLogin(
        this.phoneLoginForm.phonenumber.trim(),
        this.phoneLoginForm.smsCode.trim()
      )

      if (response && response.code === 200 && response.data?.token) {
        this.$auth.setToken(response.data.token)
        this.$auth.setCanSwitchIdentity(!!response.data.canSwitchIdentity)
        await this.afterEnterpriseTokenLogin(response.data)
      }
      // 全局拦截器已处理错误响应，这里不需要额外处理
    },
    /**
     * 企业账号密码登录：逻辑不变，不弹身份选择
     */
    async handleAccountLogin() {
      await this.$refs.accountLoginForm.validate()

      const response = await enterpriseAccountLogin(
        this.accountLoginForm.account.trim(),
        this.accountLoginForm.password
      )

      if (response && response.code === 200 && response.data?.token) {
        this.$auth.setToken(response.data.token)
        this.$auth.setCanSwitchIdentity(!!response.data.canSwitchIdentity)
        this.saveRememberedAccount(this.accountLoginForm.account.trim())
        await this.handleLoginSuccess()
      }
      // 全局拦截器已处理错误响应，这里不需要额外处理
    },
    /**
     * 企业手机号登录后：多家企业则只选企业列表；单家则自动切入
     */
    async afterEnterpriseTokenLogin(loginData = {}) {
      const enterpriseIdentities = loginData.enterpriseIdentities || []
      if (enterpriseIdentities.length > 1) {
        this.loginIdentityData = {
          enterpriseIdentities,
          currentMainBodyType: loginData.currentMainBodyType || '',
          currentMainBodyId: loginData.currentMainBodyId,
          canSwitchIdentity: loginData.canSwitchIdentity
        }
        this.identityDialogVisible = true
        return
      }
      if (enterpriseIdentities.length === 1) {
        const ent = enterpriseIdentities[0]
        const entId = ent.entId ?? ent.enterpriseId ?? ent.id
        if (entId != null && entId !== '') {
          try {
            const switchRes = await switchUser(entId)
            if (switchRes?.code === 200 && switchRes.data?.token) {
              this.$auth.clearAuth()
              this.$auth.setToken(switchRes.data.token)
              this.$auth.setCanSwitchIdentity(
                switchRes.data.canSwitchIdentity !== undefined
                  ? !!switchRes.data.canSwitchIdentity
                  : !!loginData.canSwitchIdentity
              )
            }
          } catch (e) {
            console.error('自动切换企业身份失败:', e)
          }
        }
      }
      await this.handleLoginSuccess()
    },
    handleIdentitySelectSuccess() {
      this.loginIdentityData = null
      this.handleLoginSuccess()
    },
    /**
     * 处理登录成功后的逻辑
     */
    async handleLoginSuccess() {
      const data = await this.$auth.refreshMainBody()
      
      if (data) {
        ElMessage.success('登录成功')
        this.$emit('login-success')
        
        setTimeout(() => {
          window.location.reload()
        }, RELOAD_DELAY)
      }
      // 全局拦截器已处理错误响应，这里不需要额外处理
    },
    /**
     * 忘记密码处理
     */
    handleForgotPassword() {
      this.forgotPasswordDialogVisible = true
    }
  }
}
</script>

<style lang="scss" scoped>
.enterprise-login-content {
  min-height: 300px;
  padding: 20px 0;

  @media (max-width: 768px) {
    min-height: auto;
    padding-bottom: 10px;
  }
}

.login-method-tabs {
  text-align: center;
  margin-bottom: 30px;
  font-size: 14px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }

  .method-tab {
    color: #999;
    cursor: pointer;
    padding: 0 10px;
    transition: color 0.3s;

    &:hover {
      color: #206ef4;
    }

    &.active {
      color: #206ef4;
      font-weight: 600;
    }
  }

  .method-divider {
    color: #e0e0e0;
    margin: 0 5px;
  }
}

// Phone Login
.phone-login-wrapper {
  max-width: 360px;
  margin: 0 auto;

  @media (max-width: 768px) {
    max-width: 100%;
  }

  .phone-login-form {
    :deep(.el-form-item) {
      margin-bottom: 20px;

      @media (max-width: 768px) {
        margin-bottom: 15px;
      }

      .el-input__wrapper {
        border-radius: 6px;
        border: 1px solid #DCDFE6;
        background-color: #FFFFFF;
        box-shadow: none;

        &:hover {
          border-color: #DCDFE6;
        }

        &.is-focus {
          border-color: #206ef4;
          box-shadow: 0 0 0 1px #206ef4 inset;
        }
      }

      // 当使用 append 时，输入框只保留左侧圆角
      :deep(.el-input-group) {
        .el-input__wrapper {
          border-radius: 6px 0 0 6px;
        }
      }

      .el-input__inner {
        height: 45px;
        font-size: 14px;
        color: #333;
        
        &::placeholder {
          color: #C0C4CC;
          font-size: 14px;
        }
      }

      .el-input-group__append {
        background-color: #fff;
        border-color: #DCDFE6;
        padding: 0;
        border-left: 1px solid #DCDFE6;
        border-radius: 6px; // 完整圆角，与输入框圆角一致
        
        .el-button {
          height: 45px;
          padding: 0 20px;
          margin: 0;
          border: none;
          border-radius: 6px; // 完整圆角，与输入框圆角一致
          color: #206ef4;
          font-size: 14px;
          background: transparent;
          font-weight: 400;

          &:hover:not(:disabled) {
            color: #206ef4;
            background: transparent;
          }

          &:disabled {
            color: #C0C4CC;
            background: transparent;
          }
        }
      }
    }
  }

  .phone-login-options {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 15px;
    font-size: 14px;

    .no-code-link {
      color: #206ef4;
      text-decoration: none;
      font-size: 14px;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

// Enterprise Account Login
.account-login-wrapper {
  max-width: 360px;
  margin: 0 auto;

  @media (max-width: 768px) {
    max-width: 100%;
  }

  .account-login-form {
    :deep(.el-form-item) {
      margin-bottom: 20px;

      @media (max-width: 768px) {
        margin-bottom: 15px;
      }

      .el-input__wrapper {
        border-radius: 6px;
        border: 1px solid #DCDFE6;
        background-color: #FFFFFF;
        box-shadow: none;
        transition: all 0.3s;

        &:hover {
          border-color: #C0C4CC;
        }

        &.is-focus {
          border-color: #206ef4;
          box-shadow: 0 0 0 1px #206ef4 inset;
        }
      }

      .el-input__inner {
        height: 45px;
        font-size: 14px;
        color: #333;
        
        &::placeholder {
          color: #C0C4CC;
          font-size: 14px;
        }
      }
    }
  }

  .account-login-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 15px;
    margin-bottom: 10px;
    font-size: 14px;

    :deep(.el-checkbox) {
      .el-checkbox__label {
        color: #666;
        font-size: 14px;
        padding-left: 8px;
      }

      .el-checkbox__input.is-checked .el-checkbox__inner {
        background-color: #206ef4;
        border-color: #206ef4;
      }

      &:hover .el-checkbox__inner {
        border-color: #206ef4;
      }
    }

    .forgot-password-link {
      color: #206ef4;
      text-decoration: none;
      font-size: 14px;
      transition: all 0.3s;

      &:hover {
        text-decoration: underline;
        color: #004494;
      }
    }
  }
}

.agreement-section {
  margin-top: 25px;
  margin-bottom: 20px;
  text-align: center;

  @media (max-width: 768px) {
    margin-top: 15px;
    margin-bottom: 15px;
  }

  :deep(.el-checkbox) {
    .el-checkbox__label {
      font-size: 14px;
      color: #666;
      padding-left: 8px;
    }

    .el-checkbox__input.is-checked .el-checkbox__inner {
      background-color: #206ef4;
      border-color: #206ef4;
    }

    &:hover .el-checkbox__inner {
      border-color: #206ef4;
    }
  }

  .agreement-text {
    color: #666;
  }

  .agreement-link {
    color: #206ef4;
    text-decoration: none;
    margin: 0 2px;
    transition: all 0.3s;

    &:hover {
      text-decoration: underline;
      color: #004494;
    }
  }
}

.login-button-wrapper {
  margin-top: 20px;
  text-align: center;

  .login-btn {
    width: 100%;
    max-width: 360px;
    height: 45px;
    font-size: 16px;
    background-color: #0d63f3;
    border-color: #0d63f3;
    font-weight: 500;
    transition: all 0.3s;

    @media (max-width: 768px) {
      max-width: 100%;
      height: 40px;
      font-size: 14px;
    }

    &:hover:not(:disabled) {
      background-color: #0d63f3;
      border-color: #0d63f3;
      opacity: 0.9;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(13, 99, 243, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
      opacity: 0.95;
    }

    &:disabled {
      background-color: #C0C4CC;
      border-color: #C0C4CC;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
}
</style>
